import {useIsBotModeEnabled} from '@aboutyou/data-fetcher';
import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Price} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/price.types';
import {PriceV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/price_v2.types';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {Log} from '@aboutyou/mlm';
import {useIsDirectEntryCallback} from '@aboutyou/router';
import React, {useEffect, useRef} from 'react';
import {SelectedSize} from '../../../features/SizeSelection/hooks/useSelectedSizeState';
import {useGetVidParamOrManualPreselectedSize} from '../PriceAndBrandHorizontal/useGetVidParamOrManualPreselectedSize';

/**
 * This hook logs price mismatches or sold-out state mismatches between the server initial state and the client state (after refetch) on direct entry.
 * It only logs the difference for bots.
 * This is built to catch issues SEO bots might have gotten an outdated version of the page with outdated prices or sold-out states, because
 * they are set with a high Cache TTL in cloudflare.
 *
 * For normal users, the price is expected to have minor mismatches and the price gets corrected on the client after refetch immediately.
 */
export const LogPriceDifference: React.FC<{
  price: Price | PriceV2 | undefined;
  selectedSize: SelectedSize;
  product: Product;
  isInMiniADPDialog: boolean;
}> = ({product, price, selectedSize, isInMiniADPDialog}) => {
  const botMode = useIsBotModeEnabled();

  const data = useGetRenderedPriceInfo({
    product,
    price,
    selectedSize,
    isInMiniADPDialog,
  });

  useLogPriceDiffForBots(data);

  if (!botMode) {
    return null;
  }

  return (
    <script
      id="server-rendered-price-data"
      dangerouslySetInnerHTML={{
        __html: `(${saveServerRenderedPrice.toString()})(${JSON.stringify(
          data,
        )})`,
      }}
    />
  );
};

const saveServerRenderedPrice = (props: RenderedPriceInfo) => {
  window.__SERVER_RENDERED_ADP_PRICE_DATA__ = props;
};

interface RenderedPriceInfo {
  price: number | undefined;
  isSoldOut: boolean;
  productId: number;
}

const useGetRenderedPriceInfo = ({
  product,
  price: productPrice,
  selectedSize,
  isInMiniADPDialog,
}: {
  price: Price | PriceV2 | undefined;
  selectedSize: SelectedSize;
  product: Product;
  isInMiniADPDialog: boolean;
}): RenderedPriceInfo => {
  const selectedSizeOrVidSelectedSize = useGetVidParamOrManualPreselectedSize({
    selectedSize,
    productId: product.id,
    isInMiniADPDialog,
  });
  const {isActive: isPriceOverhaulV2Active} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );
  const resolvedPriceModal =
    (selectedSizeOrVidSelectedSize?.availability?.$case === 'inStock' &&
      selectedSizeOrVidSelectedSize.availability.inStock[
        isPriceOverhaulV2Active ? 'priceV2' : 'price'
      ]) ||
    productPrice;

  const price = resolvedPriceModal?.tracker?.fullPrice;

  return {
    price,
    isSoldOut: product.isSoldOut,
    productId: product.id,
  };
};

const useLogPriceDiffForBots = ({
  productId,
  price: currentPriceInCents,
  isSoldOut: currentlySoldOut,
}: RenderedPriceInfo) => {
  const botMode = useIsBotModeEnabled();

  const getIsDirectEntry = useIsDirectEntryCallback();
  const loggedPriceDiff = useRef(false);

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    if (!botMode) {
      return;
    }
    if (!getIsDirectEntry()) {
      return;
    }
    if (loggedPriceDiff.current) {
      return;
    }

    const serverRenderedPriceData = window.__SERVER_RENDERED_ADP_PRICE_DATA__;
    if (!serverRenderedPriceData) {
      return;
    }

    const serverRenderedPriceInCents = serverRenderedPriceData.price;
    const serverRenderedSoldOut = serverRenderedPriceData.isSoldOut;

    if (currentlySoldOut !== serverRenderedSoldOut) {
      Log.unSampledLog('adp-price-mismatch', {
        reason: 'sold-out-mismatch',
        productId,
        currentlySoldOut,
        serverRenderedSoldOut,
      });
      loggedPriceDiff.current = true;
      return;
    }

    if (currentPriceInCents !== serverRenderedPriceInCents) {
      Log.unSampledLog('adp-price-mismatch', {
        reason: 'price-mismatch',
        productId,
        currentPriceInCents,
        serverRenderedPriceInCents,
      });
      loggedPriceDiff.current = true;
    }
  }, [
    botMode,
    currentPriceInCents,
    currentlySoldOut,
    getIsDirectEntry,
    productId,
  ]);

  return null;
};
